import { Box, Typography, Stack, Chip } from "@mui/material";

import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import React, { JSXElementConstructor, ReactElement, useEffect, useMemo, useState } from "react";
import { assetService, cacheService, fileService, ingestService } from "../../../Core/Api/services";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { LastIndex, UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { useAppSelector } from "../../../Redux/redux-store";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import AssetVideoResolutionChip from "../../Misc/Chips/AssetChip/asset-video-resolution-chip";
import moment from "moment";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import PanelAccordion from "../../Panels/PanelAccordion/panel-accordion";
import { GetObjectMetadataMetadataDto } from "../../../Redux/Reducers/redux-slice-asset";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { IPreviewTabContentProps } from "./preview-tabs";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import AudioTracksList from "../../AudioTracks/audio-tracks-list";
import { useTranslation } from "react-i18next";
import {
  AdminAssetSingleMetadataDto,
  AssetPanelDto,
  DbAssetObjectInfos,
} from "../../../Core/Api/Types/db-asset-object-infos";
import { SearchCard } from "../../Cards/SearchCard/SearchCard";
import DragOrderedChildren from "../../Misc/DragOrderedChildren/drag-ordered-children";

export const MetadataTab: React.FC<IPreviewTabContentProps> = (props: IPreviewTabContentProps) => {
  const { t } = useTranslation();
  const { asset, context } = props;
  const [totalSize, setTotalSize] = useState<string>("Loading...");
  let tempAssetData = useAppSelector(GetObjectMetadataMetadataDto);
  const [assetData, setAssetData] = useState<AdminAssetSingleMetadataDto | undefined>(tempAssetData);
  const [parentAssetData, setParentAssetData] = useState<DbAssetObjectInfos | null>(null);
  const [assetGlobalInfos, setAssetGlobalInfos] = useState<AssetPanelDto["globalInfos"] | null>(null);
  const aclPlaceholder = UserHasAcl(AclTypes.aclIngestPlaceholderCreate);
  const [placeholder, setPlaceholder] = useState<string | null>("");
  const platformMode = cacheService.GetPlatformMode();

  // we need to reload the asset metadata as it's not a GetGraphResponse in panel object
  useEffect(() => {
    assetService.GetObjectLightById(asset.id).then((response) => {
      if (response.success) {
        setAssetData(assetService.GetAdminAssetSingleMetadataDtoFromGraphResponse(response));
      }
    });
  }, [asset.id]);

  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();
  const frameRateAsset: number =
    asset && metadata.length > 0 ? GetMetadataByTagName(asset!, "techFrameRate", metadata)?.value : 25;
  const videoProfile = React.useMemo(() => {
    if (!asset) return "";
    return GetMetadataByTagName(asset!, "videoProfile", metadata)?.value;
  }, [asset]);

  const libelle = React.useMemo(() => {
    if (!asset) return "";
    return GetMetadataByTagName(asset!, "rc_ba_libelle", metadata)?.value;
  }, [asset?.id, metadata]);

  const id_ba = React.useMemo(() => {
    if (!asset) return "";
    return GetMetadataByTagName(asset!, "rc_ba_id", metadata)?.value;
  }, [asset?.id, metadata]);

  // Asset & Folder sizes for Drive
  useEffect(() => {
    function sizeToString(size: number): string {
      const sizeMb = size / 1024 / 1024;
      if (sizeMb > 1000) {
        const sizeGb = sizeMb / 1024;
        return sizeGb.toFixed(2) + " GB";
      } else {
        return sizeMb.toFixed(0) + " MB";
      }
    }
    if (context === PreviewContext.DriveFolderPreview || context === PreviewContext.DriveEnlarged) {
      if (asset.type === ObjectType.Folder) {
        fileService.GetFolderSize(asset.id).then((response) => {
          if (response.success) {
            if (response.size === 0) setTotalSize("Unknown");
            else {
              setTotalSize(sizeToString(response.size));
            }
          } else {
            setTotalSize("Unknown");
          }
        });
      } else {
        fileService.GetAssetSize(asset.id).then((response) => {
          if (response.success) {
            if (response.size === 0) setTotalSize("Unknown");
            else {
              setTotalSize(sizeToString(response.size));
            }
          } else {
            setTotalSize("Unknown");
          }
        });
      }
    }

    return () => {
      setTotalSize("Loading...");
    };
  }, [asset]);

  // Ingest metadata for Distrib
  useEffect(() => {
    if (asset && asset.id > 0) {
      assetService.GetAssetForAssetPanel(asset.id).then((response) => {
        if (response && response.globalInfos) {
          setAssetGlobalInfos(response?.globalInfos);
          if (response.globalInfos.placeholderRowId && UserHasAcl(AclTypes.aclIngestPlaceholderCreate))
            ingestService.GetPlaceholderById(response.globalInfos.placeholderRowId).then((response) => {
              if (response && response.placeholder) setPlaceholder(response.placeholder.name);
            });
        }
      });
    }
  }, [asset]);

  // Parent asset data for Clips & Moments (doesnt work in EA voluntarily, because of perms restriction)
  useEffect(() => {
    if (asset.type === ObjectType.VideoClip && asset.parentObjectId) {
      assetService.GetObjectLightById(asset.parentObjectId).then((response) => {
        if (response.success) {
          setParentAssetData(response.graph);
        }
      });
    }
  }, [asset]);

  const acceptanceDate = useMemo(() => {
    if (!asset || !asset.statusHistory) return null;

    let index = asset.statusHistory
      .slice()
      .reverse()
      .findIndex((x) => x.status === "Accepted");

    let count = asset.statusHistory.length - 1;
    index = index >= 0 ? count - index : index;

    if (index >= 0) return asset.statusHistory[index].date;
    else return null;
  }, [asset]);

  const ingestDate = useMemo(() => {
    if (!asset || !asset.statusHistory) return null;

    let index = LastIndex(asset.statusHistory, "status", "ParsedAndProbbed");

    if (index >= 0) return asset.statusHistory[index].date;
    else return acceptanceDate;
  }, [asset, acceptanceDate]);

  const dragSectionList = useMemo(() => {
    const childrenNodes: ReactElement<any, string | JSXElementConstructor<any>>[] = [];
    if (!parentAssetData)
      childrenNodes.push(
        <PanelAccordion defaultExpanded={true} key={"Asset informations"} title={t("common:assetInformations")}>
          <Stack spacing={1}>
            {(context === PreviewContext.DriveFolderPreview || context === PreviewContext.DriveEnlarged) && (
              <MetadataListItem title={t("common:size")}>{totalSize}</MetadataListItem>
            )}
            <MetadataListItem title={t("menu:title.wrapper")}>
              <Chip label={GetMetadataByTagName(asset!, "techWrapper", metadata)?.value.toString()} size="small" />
            </MetadataListItem>
            <MetadataListTextItem title={t("menu:title.codec")}>
              {GetMetadataByTagName(asset!, "techCodec", metadata)?.value.toString()}
            </MetadataListTextItem>
            {asset?.type === ObjectType.Video && (
              <>
                <MetadataListTextItem title={t("common:resolution")}>
                  <AssetVideoResolutionChip
                    key="video-res"
                    label={videoProfile}
                    resolution={videoProfile}
                    size="small"
                  />
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.frameRate")}>
                  {frameRateAsset?.toString() ?? "-"} fps
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.audioChannels")}>
                  {GetMetadataByTagName(asset!, "audioChannels", metadata)?.value.length}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.audioFormat")}>
                  <Chip
                    label={
                      GetMetadataByTagName(asset!, "audioChannels", metadata)?.value &&
                      GetMetadataByTagName(asset!, "audioChannels", metadata)?.value.length > 0
                        ? GetMetadataByTagName(asset!, "audioChannels", metadata)?.value[0].format.toString()
                        : ""
                    }
                    size="small"
                  />
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.chromaSampling")}>
                  {GetMetadataByTagName(asset!, "techChromaSampling", metadata)?.value.toString()}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.colorSpace")}>
                  {GetMetadataByTagName(asset!, "techColorSpace", metadata)?.value.toString()}
                </MetadataListTextItem>
              </>
            )}
          </Stack>
        </PanelAccordion>
      );
    if (assetData)
      assetData.metadatas.sections.forEach((section, sectionIndex) => {
        childrenNodes.push(
          <PanelAccordion defaultExpanded={false} key={section.name} title={section.name}>
            <Stack spacing={1}>
              {section.rows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  {row.type === BaseMetadataType.DbAudioChannelsMetadataValueInfos ? (
                    <AudioTracksList audioChannels={row.value} />
                  ) : (
                    <MetadataListTextItem key={rowIndex} title={row.label}>
                      {assetService.FormatMetadataRow(row) ?? "-"}
                    </MetadataListTextItem>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </PanelAccordion>
        );
      });

    return childrenNodes;
  }, [asset, assetData, parentAssetData, assetGlobalInfos, placeholder, metadata, videoProfile, totalSize]);

  const metadataSectionList = useMemo(() => {
    const childrenNodes: ReactElement<any, string | JSXElementConstructor<any>>[] = [];

    if (
      (context === PreviewContext.DistribCollectionPreview ||
        context === PreviewContext.DistribInventoryPreview ||
        context === PreviewContext.DistribEnlarged) &&
      assetGlobalInfos
    ) {
      // Ingest informations
      childrenNodes.push(
        <PanelAccordion defaultExpanded={true} key={"Ingest informations"} title={t("common:ingest")}>
          <Stack spacing={1}>
            <MetadataListTextItem title={t("menu:title.requestedBy")} color={"primary.text"}>
              {assetGlobalInfos.requestedBy}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.requestedAt")}>
              {assetGlobalInfos.requestedAt}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.providerAssigned")}>
              <Chip label={assetGlobalInfos.assignedProviderName} color="info" />
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.followedBy")}>
              {assetGlobalInfos.followedByUserNames}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.ingestDate")}>
              {assetGlobalInfos.ingestDate}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.uploadedVia")}>
              {assetGlobalInfos.uploadedVia !== "-" ? (
                <Chip label={assetGlobalInfos.uploadedVia} color="info" />
              ) : (
                assetGlobalInfos.uploadedVia
              )}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.acceptationDate")}>
              {assetGlobalInfos.acceptationDate}
            </MetadataListTextItem>
            <MetadataListTextItem title={t("menu:title.requestTemplate")}>{placeholder ?? "-"}</MetadataListTextItem>
          </Stack>
        </PanelAccordion>
      );
    }

    if (asset.type === ObjectType.Folder) {
      childrenNodes.push(
        <PanelAccordion
          defaultExpanded={true}
          key={"Folder informations"}
          title={t("panel:accordionTitle.folderInformations")}
        >
          <Stack spacing={1}>
            <MetadataListItem title={t("table:headerName.items")}>
              <Chip label={asset.childCount} />
            </MetadataListItem>
            <MetadataListItem title={t("menu:title.totalSize")}>{totalSize}</MetadataListItem>
          </Stack>
        </PanelAccordion>
      );
    } else if (asset.type === ObjectType.VideoClip) {
      const clipMetadata = GetMetadataByTagName(asset!, "clipInfo", metadata)?.value;
      const duration = (clipMetadata.frameOut - clipMetadata.frameIn) / clipMetadata.frameRate;
      const frameRate = clipMetadata.frameRate;

      childrenNodes.push(
        <PanelAccordion defaultExpanded={true} key={"Clip informations"} title={t("common:clipInformation")}>
          <Stack gap={1}>
            <MetadataListTextItem title={t("common:duration")}>{duration.toFixed(0)}s</MetadataListTextItem>
            <MetadataListTextItem title={t("common:frameRate")}>{frameRate}fps</MetadataListTextItem>
            <MetadataListTextItem title={t("common:createdAt")}>
              {moment(asset?.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
            </MetadataListTextItem>
          </Stack>
        </PanelAccordion>
      );

      if (parentAssetData) {
        childrenNodes.push(
          <Stack gap={1}>
            <Typography variant="subtitle1" color="text.priamry" fontWeight={600}>
              {t("common:linkedTo")}
            </Typography>
            <SearchCard asset={parentAssetData} />
          </Stack>
        );
      }
    }

    if (
      context === PreviewContext.DriveCollectionPreview ||
      context === PreviewContext.DriveFolderPreview ||
      context === PreviewContext.DriveEnlarged
    ) {
      <PanelAccordion defaultExpanded={false} key={"General Informations"} title={t("common:generalInformations")}>
        <Stack spacing={1}>
          <MetadataListTextItem title={t("common:upperCaseCreation")}>
            {asset?.creationInfos?.userName},{" "}
            {moment(asset?.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.ingestDate")}>
            {ingestDate ? moment(ingestDate).format(GlobalConst.MOMENT_DATE_FORMAT_FULL) : t("common:notYet")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.acceptanceDate")}>
            {acceptanceDate ? moment(acceptanceDate).format(GlobalConst.MOMENT_DATE_FORMAT_FULL) : t("common:notYet")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.providerAssigned")}>
            <Chip color="info" label={asset?.providerName ?? t("chip:label.notAssigned")}></Chip>
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:followers")}>
            {asset?.followedByUserNames ?? t("menu:item.noFollowers")}
          </MetadataListTextItem>
          {aclPlaceholder && placeholder !== "" && (
            <MetadataListTextItem title={t("common:placeholderName")}>{placeholder}</MetadataListTextItem>
          )}
        </Stack>
      </PanelAccordion>;
    }

    if (assetData)
      childrenNodes.push(
        <DragOrderedChildren
          dragIconHandle
          dragIconSx={{ mt: 1.5 }}
          children={dragSectionList}
          cacheListKey="metadata-tab"
        />
      );

    return <>{childrenNodes}</>;
  }, [asset, assetData, parentAssetData, assetGlobalInfos, placeholder, metadata, videoProfile, totalSize]);

  return (
    <Box py={2} px={2}>
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography component="h3" variant="subtitle2" fontWeight={600} mb={2} sx={{ wordWrap: "break-word" }}>
            {context === PreviewContext.DriveFolderPreview ||
            context === PreviewContext.DriveEnlarged ||
            context === PreviewContext.DriveCollectionPreview ? (
              <>
                {asset.name}
                <Typography variant="body1" color="text.secondary" mt={1}>
                  #{asset.id}
                </Typography>
              </>
            ) : platformMode === PlatformMode.Adam && (libelle || id_ba) ? (
              <>
                {libelle}
                <Typography color="text.secondary" mt={1}>
                  {id_ba}
                </Typography>
              </>
            ) : (
              <>
                {asset.parentTitleName ? asset.parentTitleName + " / " : ""}
                {asset.parentObject?.name}
                <Typography color="text.secondary" mt={1}>
                  {asset.name}
                </Typography>
              </>
            )}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent={"flex-start"} alignItems={"center"}>
            <AssetTypeChip type={asset!.type} />
            <Chip label={asset!.classification} />
            {cacheService.GetPlatformMode() === PlatformMode.Adam && (
              <Chip label={GetMetadataByTagName(asset, "rc_ba_id")?.value ? "Commande" : "Source"} color="accent" />
            )}
          </Stack>
        </Stack>
        <Stack>{metadataSectionList}</Stack>
      </Stack>
    </Box>
  );
};
